import { useState } from 'react';
import { Alert, App, Button, Card, Form, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { CreateFleetBody, UpdateFleetBody } from '@goelectric/shared-types';
import { fleetApi } from '../api/fleetApi';
import { FleetForm, type FleetFormState } from '../components/FleetForm';
import { describeFleetApiError } from '../utils/fleetErrors';

/**
 * Página de alta de flota (`/flotas/nuevo`, solo `SUPER_ADMIN`/`SOPORTE_N3` — el guard de
 * la ruta lo garantiza). Compone `FleetForm` con el POST: en éxito navega al detalle de la
 * flota creada; en error muestra el `message` del backend y marca el campo si lo identifica
 * (ADR 0021).
 */
export function CreateFleetPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<FleetFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goToList = () => navigate('/flotas');

  const handleSubmit = async (body: CreateFleetBody | UpdateFleetBody) => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await fleetApi.create(body as CreateFleetBody);
      message.success(`Flota ${created.name} creada.`);
      navigate(`/flotas/${encodeURIComponent(created.id)}`);
    } catch (caught) {
      const { message: detail, field } = describeFleetApiError(caught);
      setError(detail);
      if (field) {
        form.setFields([{ name: field as keyof FleetFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flotas-page">
      <div className="flotas-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de flotas"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nueva flota
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear la flota"
          description={error}
        />
      )}

      <Card>
        <FleetForm form={form} onSubmit={handleSubmit} submitting={submitting} />
      </Card>
    </div>
  );
}
